import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const PRIMARY = '#C53D3F';
const PRIMARY_LIGHT = '#F9ECEC';
const NAVY = '#1A2B4A';
const MUTED = '#6B7280';
const BORDER = '#E5E7EB';
const BG = '#F7F8FA';
const WHITE = '#FFFFFF';

const styles = {
  page: {
    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
    background: BG,
    color: NAVY,
    lineHeight: 1.6,
  },

  /* HERO */
  hero: {
    background: `linear-gradient(150deg, ${NAVY} 0%, #243557 100%)`,
    padding: '88px 24px 80px',
    textAlign: 'center',
    position: 'relative',
    overflow: 'hidden',
  },
  heroBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 18px',
    background: 'rgba(197,61,63,0.18)',
    border: '1px solid rgba(197,61,63,0.45)',
    borderRadius: 100,
    color: '#F4A0A1',
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
    marginBottom: 22,
  },
  heroDot: {
    width: 6, height: 6,
    background: PRIMARY,
    borderRadius: '50%',
    display: 'inline-block',
  },
  heroTitle: {
    fontFamily: "'Playfair Display', Georgia, serif",
    fontSize: 'clamp(2.25rem, 5vw, 3.5rem)',
    fontWeight: 700,
    color: WHITE,
    margin: '0 0 18px',
    lineHeight: 1.18,
    letterSpacing: '-0.01em',
  },
  heroDesc: {
    fontSize: 17,
    color: 'rgba(255,255,255,0.72)',
    maxWidth: 580,
    margin: '0 auto',
    fontWeight: 400,
  },
  heroAccent: {
    position: 'absolute',
    bottom: 0, left: 0, right: 0,
    height: 4,
    background: `linear-gradient(90deg, transparent, ${PRIMARY}, transparent)`,
  },

  /* LAYOUT */
  section: {
    padding: '72px 24px',
  },
  container: {
    maxWidth: 1100,
    margin: '0 auto',
  },

  /* TABS */
  tabsWrap: {
    display: 'flex',
    gap: 8,
    marginBottom: 40,
    borderBottom: `2px solid ${BORDER}`,
    overflowX: 'auto',
    paddingBottom: 0,
  },
  tabBtn: (active) => ({
    padding: '12px 24px',
    background: 'none',
    border: 'none',
    borderBottom: active ? `2.5px solid ${PRIMARY}` : '2.5px solid transparent',
    marginBottom: -2,
    color: active ? PRIMARY : MUTED,
    fontWeight: active ? 700 : 500,
    fontSize: 15,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'all 0.18s',
    fontFamily: 'inherit',
  }),

  /* CARD */
  card: {
    background: WHITE,
    borderRadius: 14,
    border: `1px solid ${BORDER}`,
    padding: '36px 40px',
    boxShadow: '0 2px 16px rgba(26,43,74,0.05)',
  },

  sectionLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '1.8px',
    textTransform: 'uppercase',
    color: PRIMARY,
    marginBottom: 8,
  },
  sectionTitle: {
    fontFamily: "'Playfair Display', Georgia, serif",
    fontSize: 'clamp(1.6rem, 3vw, 2.1rem)',
    fontWeight: 700,
    color: NAVY,
    margin: '0 0 10px',
    lineHeight: 1.25,
  },
  sectionDesc: {
    fontSize: 15,
    color: MUTED,
    maxWidth: 620,
    marginBottom: 36,
  },
  divider: {
    height: 1,
    background: BORDER,
    margin: '28px 0',
    border: 'none',
  },

  /* TIMELINE */
  timelineItem: {
    display: 'flex',
    gap: 24,
    marginBottom: 8,
  },
  timelineLeft: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flexShrink: 0,
  },
  stepBubble: {
    width: 40,
    height: 40,
    borderRadius: '50%',
    background: PRIMARY,
    color: WHITE,
    fontWeight: 700,
    fontSize: 15,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    boxShadow: `0 0 0 4px ${PRIMARY_LIGHT}`,
  },
  timelineLine: {
    flex: 1,
    width: 2,
    background: BORDER,
    margin: '8px 0',
    minHeight: 28,
  },
  timelineBody: {
    flex: 1,
    paddingBottom: 32,
  },
  stepTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: NAVY,
    margin: '6px 0 6px',
  },
  stepDesc: {
    fontSize: 14,
    color: MUTED,
    lineHeight: 1.7,
    marginBottom: 12,
  },
  metaRow: {
    display: 'flex',
    gap: 20,
    flexWrap: 'wrap',
  },
  metaPill: {
    fontSize: 12,
    padding: '4px 12px',
    background: BG,
    border: `1px solid ${BORDER}`,
    borderRadius: 100,
    color: MUTED,
    fontWeight: 500,
  },
  metaKey: {
    fontWeight: 700,
    color: NAVY,
  },

  /* REQUIREMENTS GRID */
  reqGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: 20,
    marginBottom: 32,
  },
  reqCard: {
    background: WHITE,
    border: `1px solid ${BORDER}`,
    borderRadius: 12,
    padding: '28px 28px',
    transition: 'box-shadow 0.2s, border-color 0.2s',
  },
  reqHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    marginBottom: 20,
    paddingBottom: 16,
    borderBottom: `1px solid ${BORDER}`,
  },
  reqIconBox: {
    width: 44,
    height: 44,
    borderRadius: 10,
    background: PRIMARY_LIGHT,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 22,
    flexShrink: 0,
  },
  reqTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: NAVY,
    marginBottom: 2,
  },
  reqAge: {
    fontSize: 12,
    color: PRIMARY,
    fontWeight: 600,
  },
  reqItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    padding: '7px 0',
    borderBottom: `1px solid ${BG}`,
    fontSize: 13.5,
    color: MUTED,
  },
  checkDot: {
    width: 18,
    height: 18,
    borderRadius: '50%',
    background: '#E8F7F0',
    border: '1.5px solid #34D399',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginTop: 1,
    fontSize: 10,
    color: '#059669',
    fontWeight: 700,
  },

  /* NOTES BOX */
  notesBox: {
    background: '#F0F4FF',
    border: `1px solid #C7D3F0`,
    borderLeft: `4px solid ${NAVY}`,
    borderRadius: 10,
    padding: '24px 28px',
  },
  notesTitle: {
    fontWeight: 700,
    color: NAVY,
    fontSize: 15,
    marginBottom: 14,
  },
  noteItem: {
    fontSize: 13.5,
    color: '#374151',
    marginBottom: 10,
    paddingLeft: 14,
    position: 'relative',
    lineHeight: 1.6,
  },

  /* FEE TABLE */
  feeSection: {
    marginBottom: 36,
  },
  feeTableTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: NAVY,
    marginBottom: 12,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  tableWrap: {
    overflowX: 'auto',
    borderRadius: 10,
    border: `1px solid ${BORDER}`,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: 480,
    fontSize: 14,
  },
  th: {
    background: NAVY,
    color: WHITE,
    padding: '11px 18px',
    textAlign: 'left',
    fontWeight: 600,
    fontSize: 12,
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
  },
  td: {
    padding: '12px 18px',
    color: MUTED,
    borderBottom: `1px solid ${BORDER}`,
    fontWeight: 400,
  },
  tdTotal: {
    padding: '12px 18px',
    color: PRIMARY,
    borderBottom: `1px solid ${BORDER}`,
    fontWeight: 700,
  },
  feeInfoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: 20,
    marginBottom: 28,
  },
  infoCard: {
    background: WHITE,
    border: `1px solid ${BORDER}`,
    borderTop: `3px solid ${PRIMARY}`,
    borderRadius: 10,
    padding: '24px 24px',
  },
  infoTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: NAVY,
    marginBottom: 14,
  },
  infoItem: {
    fontSize: 13,
    color: MUTED,
    padding: '7px 0',
    borderBottom: `1px solid ${BG}`,
    paddingLeft: 12,
    position: 'relative',
    lineHeight: 1.55,
  },
  feeNote: {
    background: PRIMARY_LIGHT,
    border: `1px solid rgba(197,61,63,0.18)`,
    borderRadius: 8,
    padding: '14px 18px',
    fontSize: 13,
    color: '#7C2A2B',
    lineHeight: 1.6,
  },

  /* FAQ */
  faqItem: (open) => ({
    border: `1px solid ${open ? PRIMARY : BORDER}`,
    borderRadius: 10,
    overflow: 'hidden',
    marginBottom: 10,
    transition: 'border-color 0.2s',
    background: WHITE,
  }),
  faqBtn: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 22px',
    background: 'none',
    border: 'none',
    textAlign: 'left',
    cursor: 'pointer',
    fontFamily: 'inherit',
    gap: 12,
  },
  faqQ: {
    fontSize: 15,
    fontWeight: 600,
    color: NAVY,
    flex: 1,
  },
  faqToggle: (open) => ({
    width: 28,
    height: 28,
    borderRadius: '50%',
    background: open ? PRIMARY : BG,
    color: open ? WHITE : MUTED,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 18,
    fontWeight: 400,
    flexShrink: 0,
    transition: 'background 0.2s, color 0.2s',
    lineHeight: 1,
  }),
  faqA: {
    fontSize: 14,
    color: MUTED,
    padding: '0 22px 18px',
    lineHeight: 1.75,
  },

  /* CONTACT BOX */
  contactBox: {
    background: NAVY,
    borderRadius: 14,
    padding: '40px 36px',
    textAlign: 'center',
    marginTop: 36,
  },
  contactTitle: {
    fontFamily: "'Playfair Display', Georgia, serif",
    fontSize: 24,
    fontWeight: 700,
    color: WHITE,
    marginBottom: 8,
  },
  contactDesc: {
    fontSize: 15,
    color: 'rgba(255,255,255,0.65)',
    marginBottom: 28,
  },
  contactGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 14,
    marginBottom: 28,
  },
  contactItem: {
    background: 'rgba(255,255,255,0.07)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 10,
    padding: '16px 12px',
    textAlign: 'center',
  },
  contactIcon: { fontSize: 22, marginBottom: 6 },
  contactLabel: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '1.2px',
    textTransform: 'uppercase',
    color: 'rgba(255,255,255,0.45)',
    marginBottom: 4,
  },
  contactVal: { fontSize: 13, color: WHITE, fontWeight: 500 },

  /* CTA BUTTON */
  btn: (variant = 'primary') => ({
    display: 'inline-block',
    padding: '12px 28px',
    borderRadius: 8,
    fontWeight: 700,
    fontSize: 14,
    textDecoration: 'none',
    letterSpacing: '0.3px',
    transition: 'opacity 0.15s, transform 0.15s',
    background: variant === 'primary' ? PRIMARY : WHITE,
    color: variant === 'primary' ? WHITE : PRIMARY,
    border: variant === 'primary' ? 'none' : `2px solid ${PRIMARY}`,
  }),

  /* ACTIONS SECTION */
  actionsSection: {
    background: WHITE,
    padding: '72px 24px',
    borderTop: `1px solid ${BORDER}`,
  },
  actionsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
    gap: 20,
    marginTop: 40,
  },
  actionCard: (featured) => ({
    background: featured ? NAVY : WHITE,
    border: featured ? 'none' : `1px solid ${BORDER}`,
    borderRadius: 14,
    padding: '36px 28px',
    textAlign: 'center',
    position: 'relative',
    transition: 'transform 0.2s, box-shadow 0.2s',
  }),
  actionCardIcon: (featured) => ({
    fontSize: 36,
    marginBottom: 16,
    opacity: featured ? 1 : 0.85,
  }),
  actionCardTitle: (featured) => ({
    fontFamily: "'Playfair Display', Georgia, serif",
    fontSize: 18,
    fontWeight: 700,
    color: featured ? WHITE : NAVY,
    marginBottom: 8,
  }),
  actionCardDesc: (featured) => ({
    fontSize: 13.5,
    color: featured ? 'rgba(255,255,255,0.65)' : MUTED,
    marginBottom: 22,
    lineHeight: 1.6,
  }),
  featuredBadge: {
    position: 'absolute',
    top: -11,
    left: '50%',
    transform: 'translateX(-50%)',
    background: PRIMARY,
    color: WHITE,
    fontSize: 11,
    fontWeight: 700,
    padding: '4px 14px',
    borderRadius: 100,
    letterSpacing: '1px',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
  },
};

const faqs = [
  { question: "When does the academic year begin?", answer: "The academic year runs from September to July, divided into three terms. Applications are accepted year-round, but we recommend applying by July for guaranteed placement." },
  { question: "What is the application deadline?", answer: "While we accept applications throughout the year, priority consideration is given to applications received by July 31st for the upcoming academic year." },
  { question: "Do you offer scholarships or financial aid?", answer: "Yes, we offer merit-based scholarships and need-based financial aid. Scholarships are awarded based on academic excellence and character. Financial aid applications are reviewed confidentially." },
  { question: "What are the school hours?", answer: "Pre-School: 7:30 AM – 12:30 PM | Primary & JHS: 7:30 AM – 2:30 PM. Extended care is available until 4:30 PM for an additional fee." },
  { question: "Is transportation provided?", answer: "Yes, we offer school bus services covering major routes in Accra and surrounding areas. Transportation fees vary by location and are separate from tuition." },
  { question: "What is your teacher-to-student ratio?", answer: "We maintain small class sizes: Pre-School (1:8), Primary (1:20), and JHS (1:25) to ensure personalized attention for every student." },
  { question: "Can international students apply?", answer: "Absolutely! We welcome international students. Additional documentation includes student visa, guardian information, and English proficiency assessment if applicable." },
  { question: "What curriculum do you follow?", answer: "We follow the British curriculum for Pre-School and JHS, while our Primary program uniquely blends the Ghana Education Service curriculum with British educational standards." },
];

const TabBtn = ({ label, icon, id, active, onClick }) => (
  <button style={styles.tabBtn(active)} onClick={() => onClick(id)}>
    <span style={{ marginRight: 6 }}>{icon}</span> {label}
  </button>
);

const ProcessTab = () => {
  const steps = [
    { n: 1, title: "Inquiry & School Visit", desc: "Contact our admissions office to schedule a campus tour. Experience our facilities, meet our educators, and see students in action.", meta: [{ k: "Duration", v: "1–2 days" }, { k: "Contact", v: "starhacsinternational@gmail.com" }] },
    { n: 2, title: "Submit Application", desc: "Complete our online application form and submit all required documents. Our team responds within 3–5 business days with next steps.", meta: [{ k: "Duration", v: "3–5 days" }, { k: "Fee", v: "GH₵ 100 (non-refundable)" }] },
    { n: 3, title: "Assessment & Interview", desc: "Students participate in age-appropriate assessments for proper grade placement. Parents meet with school leadership to discuss goals and expectations.", meta: [{ k: "Duration", v: "1 week" }, { k: "Format", v: "In-person or virtual" }] },
    { n: 4, title: "Admission Decision", desc: "Receive decision via email. Accepted students get an enrollment package including registration forms, fee structure, and uniform requirements.", meta: [{ k: "Duration", v: "2–3 days" }, { k: "Decision", v: "Email notification" }] },
    { n: 5, title: "New Student Orientation", desc: "Attend our orientation program — meet teachers and classmates, tour the campus, and prepare for a successful first day.", meta: [{ k: "Duration", v: "1 day" }, { k: "When", v: "Week before school starts" }] },
  ];
  return (
    <div>
      <p style={styles.sectionLabel}>Step by Step</p>
      <h2 style={styles.sectionTitle}>Admission Process</h2>
      <p style={styles.sectionDesc}>Our streamlined process is designed to make joining Star Hacs International School as smooth as possible.</p>
      <div style={{ marginBottom: 40 }}>
        {steps.map((s, i) => (
          <div key={s.n} style={styles.timelineItem}>
            <div style={styles.timelineLeft}>
              <div style={styles.stepBubble}>{s.n}</div>
              {i < steps.length - 1 && <div style={styles.timelineLine} />}
            </div>
            <div style={styles.timelineBody}>
              <div style={styles.stepTitle}>{s.title}</div>
              <p style={styles.stepDesc}>{s.desc}</p>
              <div style={styles.metaRow}>
                {s.meta.map(m => (
                  <span key={m.k} style={styles.metaPill}><span style={styles.metaKey}>{m.k}:</span> {m.v}</span>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
      <div style={{ background: PRIMARY_LIGHT, border: `1px solid rgba(197,61,63,0.2)`, borderRadius: 12, padding: '28px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 20, fontWeight: 700, color: NAVY, marginBottom: 4 }}>Ready to begin?</div>
          <p style={{ fontSize: 14, color: MUTED, margin: 0 }}>Take the first step toward joining our vibrant community.</p>
        </div>
        <Link to="/contact" style={styles.btn('primary')}>Start Application →</Link>
      </div>
    </div>
  );
};

const RequirementsTab = () => {
  const levels = [
    { icon: '🎨', title: 'Pre-School', age: 'Ages 2–5 years', items: ['Completed application form', 'Birth certificate (original & copy)', 'Up-to-date immunization records', '2 recent passport-size photographs', 'Medical examination report', 'Previous school reports (if applicable)'] },
    { icon: '📚', title: 'Primary School', age: 'Grades 1–6 (Ages 6–11)', items: ['Completed application form', 'Birth certificate (original & copy)', 'Immunization records', '2 recent passport-size photographs', 'Last 2 academic term reports', 'Transfer certificate (if transferring)', 'Entrance assessment (scheduled by school)'] },
    { icon: '🎓', title: 'Junior High School', age: 'JHS 1 (Ages 12–14)', items: ['Completed application form', 'Birth certificate (original & copy)', 'Primary school completion certificate', 'BECE results (if available)', '2 recent passport-size photographs', 'Academic transcripts from previous school', 'Placement test (Math & English)'] },
  ];
  return (
    <div>
      <p style={styles.sectionLabel}>Documentation</p>
      <h2 style={styles.sectionTitle}>Admission Requirements</h2>
      <p style={styles.sectionDesc}>Please ensure all documents are prepared before beginning the application process. Originals must be presented for verification.</p>
      <div style={styles.reqGrid}>
        {levels.map(l => (
          <div key={l.title} style={styles.reqCard}>
            <div style={styles.reqHeader}>
              <div style={styles.reqIconBox}>{l.icon}</div>
              <div>
                <div style={styles.reqTitle}>{l.title}</div>
                <div style={styles.reqAge}>{l.age}</div>
              </div>
            </div>
            <div>
              {l.items.map(item => (
                <div key={item} style={styles.reqItem}>
                  <div style={styles.checkDot}>✓</div>
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div style={styles.notesBox}>
        <div style={styles.notesTitle}>📌 Important Information</div>
        {[
          ['Document Verification', 'Original documents must be presented for verification. Certified copies will be retained in the student\'s file.'],
          ['International Students', 'Additional documentation required including valid student visa, guardian information in Ghana, and English proficiency certificate (if applicable).'],
          ['Application Fee', 'GH₵ 100 (non-refundable) — covers processing and assessment costs.'],
          ['Incomplete Applications', 'Will not be processed until all required documents are submitted.'],
        ].map(([k, v]) => (
          <div key={k} style={styles.noteItem}>
            <span style={{ position: 'absolute', left: 0, color: NAVY }}>•</span>
            <strong style={{ color: NAVY }}>{k}:</strong> {v}
          </div>
        ))}
      </div>
    </div>
  );
};

const FeesTab = () => {
  const tables = [
    { title: 'Pre-School', rows: [['Toddlers', 'GH₵ 1,200', 'GH₵ 200', 'GH₵ 1,400'], ['Reception 1 & 2', 'GH₵ 1,800', 'GH₵ 300', 'GH₵ 2,100']] },
    { title: 'Primary School', rows: [['Grade 1–3', 'GH₵ 2,000', 'GH₵ 350', 'GH₵ 2,350'], ['Grade 4–6', 'GH₵ 2,300', 'GH₵ 400', 'GH₵ 2,700']] },
    { title: 'Junior High School', rows: [['JHS 1', 'GH₵ 2,800', 'GH₵ 500', 'GH₵ 3,300']] },
  ];
  return (
    <div>
      <p style={styles.sectionLabel}>Transparency</p>
      <h2 style={styles.sectionTitle}>Fee Structure 2024/2025</h2>
      <p style={styles.sectionDesc}>Fees are charged per term (3 terms per academic year). Various payment plans are available.</p>
      <div style={{ marginBottom: 36 }}>
        {tables.map(t => (
          <div key={t.title} style={styles.feeSection}>
            <div style={styles.feeTableTitle}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: PRIMARY, display: 'inline-block' }} />
              {t.title}
            </div>
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    {['Level / Grade', 'Tuition / Term', 'Development Fee', 'Total / Term'].map(h => (
                      <th key={h} style={styles.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {t.rows.map((r, i) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? WHITE : BG }}>
                      {r.map((cell, ci) => (
                        <td key={ci} style={ci === 3 ? styles.tdTotal : styles.td}>{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
      <div style={styles.feeInfoGrid}>
        <div style={styles.infoCard}>
          <div style={styles.infoTitle}>💳 Payment Options & Discounts</div>
          {['Flexible payment plans available (discuss with admissions office)', '5% discount for full annual payment in advance', '10% sibling discount for second and subsequent children', 'Payment methods: Bank transfer, Mobile Money, Cash, Cheque', 'Payment deadline: First week of each term'].map(it => (
            <div key={it} style={styles.infoItem}><span style={{ position: 'absolute', left: 0, color: PRIMARY, fontWeight: 700 }}>•</span>{it}</div>
          ))}
        </div>
        <div style={styles.infoCard}>
          <div style={styles.infoTitle}>📚 Additional Costs</div>
          {['Uniforms: GH₵ 250 – GH₵ 400 (one-time purchase)', 'Textbooks & Materials: GH₵ 150 – GH₵ 300 per term', 'Extra-curricular Activities: GH₵ 50 – GH₵ 150 per term', 'School Bus (optional): GH₵ 300 – GH₵ 600 per term', 'After-school Care: GH₵ 200 per term (until 4:30 PM)'].map(it => (
            <div key={it} style={styles.infoItem}><span style={{ position: 'absolute', left: 0, color: PRIMARY, fontWeight: 700 }}>•</span>{it}</div>
          ))}
        </div>
      </div>
      <div style={styles.feeNote}><strong>Note:</strong> Fees are subject to annual review. Parents will be notified of changes at least one term in advance. All fees must be settled before students can participate in exams or receive report cards.</div>
    </div>
  );
};

const FaqTab = () => {
  const [open, setOpen] = useState(null);
  const toggle = (i) => setOpen(open === i ? null : i);
  return (
    <div>
      <p style={styles.sectionLabel}>Common Questions</p>
      <h2 style={styles.sectionTitle}>Frequently Asked Questions</h2>
      <p style={styles.sectionDesc}>Find answers to common questions about admissions and school operations.</p>
      <div style={{ marginBottom: 32 }}>
        {faqs.map((f, i) => (
          <div key={i} style={styles.faqItem(open === i)}>
            <button style={styles.faqBtn} onClick={() => toggle(i)}>
              <span style={styles.faqQ}>{f.question}</span>
              <span style={styles.faqToggle(open === i)}>{open === i ? '−' : '+'}</span>
            </button>
            {open === i && <div style={styles.faqA}>{f.answer}</div>}
          </div>
        ))}
      </div>
      <div style={styles.contactBox}>
        <div style={styles.contactTitle}>Still have questions?</div>
        <p style={styles.contactDesc}>Our admissions team is ready to assist you.</p>
        <div style={styles.contactGrid}>
          {[{ icon: '📞', label: 'Phone', val: '+233 59 896 8300' }, { icon: '✉️', label: 'Email', val: 'starhacsinternational@gmail.com' }, { icon: '🕐', label: 'Office Hours', val: 'Mon–Fri, 8:00 AM – 4:00 PM' }].map(c => (
            <div key={c.label} style={styles.contactItem}>
              <div style={styles.contactIcon}>{c.icon}</div>
              <div style={styles.contactLabel}>{c.label}</div>
              <div style={styles.contactVal}>{c.val}</div>
            </div>
          ))}
        </div>
        <Link to="/contact" style={{ ...styles.btn('primary'), display: 'inline-block' }}>Contact Admissions →</Link>
      </div>
    </div>
  );
};

const Admissions = () => {
  const [tab, setTab] = useState('process');
  const tabs = [
    { id: 'process', label: 'Process', icon: '📋' },
    { id: 'requirements', label: 'Requirements', icon: '📚' },
    { id: 'fees', label: 'Fees', icon: '💰' },
    { id: 'faq', label: 'FAQ', icon: '❓' },
  ];
  return (
    <div style={styles.page}>
      {/* HERO */}
      <section style={styles.hero}>
        <div style={{ position: 'absolute', top: -80, right: -80, width: 320, height: 320, borderRadius: '50%', background: 'rgba(197,61,63,0.08)', pointerEvents: 'none' }} />
        <div style={{ position: 'relative', zIndex: 2 }}>
          <div style={styles.heroBadge}><span style={styles.heroDot} /> Admissions</div>
          <h1 style={styles.heroTitle}>Join Our Community</h1>
          <p style={styles.heroDesc}>Begin your educational journey at Star Hacs International School — where excellence meets opportunity.</p>
        </div>
        <div style={styles.heroAccent} />
      </section>

      {/* MAIN CONTENT */}
      <section style={styles.section}>
        <div style={styles.container}>
          {/* TABS NAV */}
          <div style={styles.tabsWrap}>
            {tabs.map(t => (
              <TabBtn key={t.id} id={t.id} label={t.label} icon={t.icon} active={tab === t.id} onClick={setTab} />
            ))}
          </div>

          {/* TAB CONTENT */}
          <div style={styles.card}>
            {tab === 'process' && <ProcessTab />}
            {tab === 'requirements' && <RequirementsTab />}
            {tab === 'fees' && <FeesTab />}
            {tab === 'faq' && <FaqTab />}
          </div>
        </div>
      </section>

      {/* QUICK ACTIONS */}
      <section style={styles.actionsSection}>
        <div style={{ ...styles.container, textAlign: 'center' }}>
          <p style={{ ...styles.sectionLabel, textAlign: 'center' }}>Next Steps</p>
          <h2 style={{ ...styles.sectionTitle, textAlign: 'center' }}>Take Your Next Step</h2>
          <p style={{ ...styles.sectionDesc, margin: '0 auto' }}>Choose how you'd like to proceed with your application journey.</p>
          <div style={styles.actionsGrid}>
            {[
              { icon: '📅', title: 'Schedule a Visit', desc: 'Experience our campus and meet our educators in person', btn: 'Book Campus Tour', to: '/contact', featured: false },
              { icon: '📝', title: 'Apply Online', desc: 'Start your application today and secure your place', btn: 'Begin Application', to: '/application', featured: true },
              { icon: '💬', title: 'Speak with Admissions', desc: 'Get personalized guidance tailored to your situation', btn: 'Contact Us', to: '/contact', featured: false },
            ].map(a => (
              <div key={a.title} style={styles.actionCard(a.featured)}>
                {a.featured && <div style={styles.featuredBadge}>Most Popular</div>}
                <div style={styles.actionCardIcon(a.featured)}>{a.icon}</div>
                <div style={styles.actionCardTitle(a.featured)}>{a.title}</div>
                <p style={styles.actionCardDesc(a.featured)}>{a.desc}</p>
                <Link
                  to={a.to}
                  style={{
                    display: 'inline-block',
                    padding: '10px 24px',
                    borderRadius: 8,
                    fontWeight: 700,
                    fontSize: 13,
                    textDecoration: 'none',
                    border: a.featured ? 'none' : `2px solid ${BORDER}`,
                    background: a.featured ? PRIMARY : WHITE,
                    color: a.featured ? WHITE : NAVY,
                  }}
                >
                  {a.btn}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700&family=DM+Sans:wght@400;500;700&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        @media (max-width: 640px) {
          .admissions-actions-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
};

export default Admissions;